/**
 * GLAMRDiP shared drag-to-scroll primitive.
 * Joel 2026-04-29.
 *
 * Auto-applies pointer-event drag-to-scroll behaviour to any element
 * marked with `data-gd-drag-scroll`. Single primitive consumed by every
 * horizontal carousel on the site so the click-vs-drag threshold,
 * native-drag prevention, and pointer-capture lifecycle are identical
 * everywhere.
 *
 *   <div class="…__track" data-gd-drag-scroll
 *        style="overflow-x: auto; cursor: grab;">
 *     …cards…
 *   </div>
 *
 * Behaviour:
 *   - Mouse pointer only — touch + pen retain native scrolling.
 *   - 5 px movement threshold separates click from drag. A drag
 *     swallows the trailing `click` event in the capture phase via a
 *     one-shot listener (80 ms timestamp window) so an `<a>` card's
 *     navigation doesn't fire after a real drag.
 *   - Pointer capture is DEFERRED until movement exceeds the
 *     threshold (Joel 2026-05-06). Capturing on pointerdown
 *     redirected the synthesised click to the captured element
 *     (track) instead of the original card anchor, so a clean
 *     no-drag click had no anchor target → no navigation.
 *     Capturing only after a real drag starts keeps clean clicks
 *     routed to their original target while still binding the
 *     gesture to the track when the user actually drags out of
 *     the element bounds.
 *   - rAF coalesces pointermove → at-most-one scrollLeft write per
 *     frame, no jank.
 *   - dragstart is suppressed on the track so a child <img>'s native
 *     HTML5 image drag never fires (no ghost preview, no drop-to-
 *     desktop). Defence-in-depth alongside the CSS user-drag:none
 *     and the markup draggable="false" attribute.
 *   - During active drag the track gets `.is-gd-dragging` so
 *     `cursor: grabbing` + `scroll-behavior: auto` + neutralised
 *     `scroll-snap-type` can be applied via CSS.
 *
 * Page-speed: zero work until DOMContentLoaded; per-track cost is
 * 4 listeners + a shared closure. Re-runs on shopify:section:load
 * so theme-editor section reloads pick up new tracks.
 */
(() => {
  'use strict';
  const THRESHOLD = 5;

  function init(track) {
    if (track.dataset.gdDragScrollInit === '1') return;
    track.dataset.gdDragScrollInit = '1';

    let drag = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Joel 2026-04-30 (desktop click fix): DO NOT call e.preventDefault()
      // on pointerdown. preventDefault here suppresses the subsequent
      // click event on desktop, breaking click handlers on every clickable
      // image / video tile / card inside drag-to-scroll containers
      // (community gallery, salon-kit reels, etc.). The native image-drag
      // ghost is already disabled by the global `img { user-drag: none }`
      // rule in glamrdip-tokens.css.liquid + the dragstart suppression at
      // the bottom of this init(). Click vs drag disambiguation runs in
      // end() below: if total movement exceeded THRESHOLD, swallow the
      // click that follows pointerup; otherwise let it through.
      drag = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startScrollLeft: track.scrollLeft,
        latestX: e.clientX,
        moved: false,
        captured: false,
        raf: false,
      };
      // NO setPointerCapture here. Capturing on pointerdown redirects
      // the synthesised click event to the capturing element (track),
      // so a clean no-drag click never reaches the card anchor →
      // navigation never fires. Capture is deferred to the
      // pointermove handler below, set the first time movement
      // exceeds THRESHOLD.
    });

    track.addEventListener('pointermove', (e) => {
      if (!drag || drag.pointerId !== e.pointerId) return;
      drag.latestX = e.clientX;
      const dx = drag.latestX - drag.startX;
      if (Math.abs(dx) > THRESHOLD) drag.moved = true;
      if (!drag.moved) return;
      // First move past THRESHOLD: NOW capture the pointer to the
      // track. Capturing here (rather than on pointerdown) keeps
      // clean clicks routed to their original target — see the
      // comment on the pointerdown handler. Once captured, the
      // gesture stays bound to the track even if the user drags
      // outside its bounds. Idempotent via drag.captured flag so
      // we only call setPointerCapture once per gesture.
      if (!drag.captured) {
        drag.captured = true;
        try { track.setPointerCapture(e.pointerId); } catch {}
      }
      // preventDefault on pointermove (only AFTER threshold crossed) is
      // safe — it just stops native text-selection during the drag,
      // doesn't affect click events. The click-vs-drag suppression
      // remains owned by end()'s post-gesture threshold check.
      e.preventDefault();
      track.classList.add('is-gd-dragging');
      if (drag.raf) return;
      drag.raf = true;
      window.requestAnimationFrame(() => {
        if (!drag) return;
        drag.raf = false;
        track.scrollLeft = drag.startScrollLeft - (drag.latestX - drag.startX);
      });
    });

    function end(e) {
      if (!drag || drag.pointerId !== e.pointerId) return;
      const wasMoved = drag.moved;
      const wasCaptured = drag.captured;
      if (wasCaptured) {
        try { track.releasePointerCapture(drag.pointerId); } catch {}
      }
      track.classList.remove('is-gd-dragging');
      drag = null;
      if (wasMoved) {
        // Joel 2026-05-06: 80 ms timestamp-window swallow. Browser-
        // synthesised trailing clicks after a captured drag arrive
        // within a few ms of pointerup; deliberate human clicks have
        // ≥150 ms reaction time. The window matched against the same
        // 80 ms removal timeout means the listener never lingers
        // past the synthesised-click range. With pointer capture
        // now deferred until the gesture is a real drag, the
        // captured-target click redirect only happens AFTER drag
        // crossed threshold — exactly the gesture this swallow is
        // here to suppress.
        const upTime = e.timeStamp;
        const swallow = (ev) => {
          if (ev.timeStamp - upTime <= 80) {
            ev.stopPropagation();
            ev.preventDefault();
          }
        };
        track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => track.removeEventListener('click', swallow, true), 80);
      }
    }
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', end);

    // Defence-in-depth against native HTML5 image drag.
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  function initAll() {
    document.querySelectorAll('[data-gd-drag-scroll]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
